import React, { useEffect, type FC, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import { Button, Space, Title } from '@g2u/ui-kit';
import { useMutation, useQuery } from 'react-query';
import { Helmet } from 'react-helmet-async';
import { observer } from 'mobx-react-lite';
import { Form } from 'antd';

import {
  HomeAddressFields,
  BillingAddressFields,
  AccountTypeFields,
  PrimaryContactFields,
  SecondaryContactFields,
  HearAboutUsFields,
  AccountNotesFields,
  Content,
} from 'components';
import { getHearAboutUs, postCustomer } from 'queries';
import { AccountType, Condition, type JsonDataItem } from 'types';
import { Toast, parseErrors, validateFormSilently } from 'helpers';
import styles from './create.module.scss';

export const CustomerCreatePage: FC = observer(() => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [accountType, setAccountType] = useState<string>(AccountType.Personal);
  const initialData = location.state?.formData;
  const initialEmail = initialData?.searchBy === 'email' ? initialData?.searchedValue : undefined;
  const initialMobile = initialData?.searchBy === 'mobile' ? initialData?.searchedValue : undefined;

  const { data: marketingOptions } = useQuery(['marketing-options'], getHearAboutUs, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: createCustomer, isLoading: creatingCustomer } = useMutation(postCustomer, {
    onSuccess: (data) => {
      form.resetFields();
      Toast.show({
        description: `The new customer file #${data.ID} has been created. `,
        type: Toast.Type.Success,
      });
      datadogRum.setGlobalContextProperty('custid', String(data.ID));

      // check if come form the callback
      if (location.state?.callbackId) {
        navigate('/tasks/callbacks', {
          state: {
            page: location.state.page,
            expandedRowId: location.state.callbackId,
            linkCustomerId: data.ID,
          },
        });
      } else {
        navigate(`/customer/edit/${data.ID}`);
      }
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const handleFormChange = (changedField: JsonDataItem) => {
    setAccountType(changedField.accountType || accountType);
    // enable "Create" button
    void validateFormSilently(form, setIsFormValid);
  };

  const handleFormFinish = (data: JsonDataItem) => {
    createCustomer({
      type: data.accountType,
      title: data.title,
      firstName: data.firstName,
      preferredName: data.preferredName,
      lastName: data.lastName,
      email: data.email,
      mobileNumber: data.mobileNumber,
      phoneNumber: data.phoneNumber,
      secondaryContact: data.secondaryContact,
      secondaryMobileNumber: data.secondaryMobileNumber,
      secondaryFirstName: data.secondaryFirstName,
      secondaryLastName: data.secondaryLastName,
      abn: data.abn === '' ? undefined : data.abn,
      companyName: data.companyName,
      homeAddressLine1: data.homeAddressLine1,
      homeAddressLine2: data.homeAddressLine2,
      homeAddressSuburb: data.homeAddressSuburb,
      homeAddressState: data.homeAddressState,
      homeAddressPostcode: data.homeAddressPostcode,
      sameBillingAddress: data.sameBillingAddress ? Condition.Yes : Condition.No,
      postalAddressLine1: data.postalAddressLine1,
      postalAddressLine2: data.postalAddressLine2,
      postalAddressSuburb: data.postalAddressSuburb,
      postalAddressState: data.postalAddressState,
      postalAddressPostcode: data.postalAddressPostcode,
      accessNotes: data.accessNotes,
      specialNotes: data.specialNotes,
      hearAboutUs: data.hearAboutUs,
      billTo: data.billTo,
    });
  };

  useEffect(() => {
    void validateFormSilently(form, setIsFormValid);
  }, []);

  return (
    <Content.$ dataTestId="create-customer-page" className={styles.create}>
      <Helmet>
        <title>Create Customer</title>
      </Helmet>
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Large}>
        <Title.$ level={4} noMargin className={styles.create__tile}>
          New Customer File
        </Title.$>
        <Form
          form={form}
          colon={false}
          layout="horizontal"
          onValuesChange={handleFormChange}
          onFinish={handleFormFinish}
        >
          <Space.$ size={Space.Size.Large} direction={Space.Direction.Vertical}>
            <AccountTypeFields.$ customer={null} />
            <PrimaryContactFields.$
              form={form}
              customer={null}
              initialValues={{ email: initialEmail, mobile: initialMobile }}
              isBusinessAccount={accountType === AccountType.Company}
            />
            <SecondaryContactFields.$ customer={null} />
            <HomeAddressFields.$ form={form} customer={null} location={null} />
            <BillingAddressFields.$ form={form} customer={null} />
            <AccountNotesFields.$ hasSpecialNote />
            <HearAboutUsFields.$ options={marketingOptions?.data} required />
            <Button.$
              type={Button.Type.Primary}
              dataTestId="page-next-btn"
              disabled={!isFormValid}
              onClick={() => {
                form.submit();
              }}
              loading={creatingCustomer}
              style={{ float: 'right' }}
            >
              Create new customer
            </Button.$>
          </Space.$>
        </Form>
      </Space.$>
    </Content.$>
  );
});
